<script setup>
import {
  ref,
  reactive,
  defineEmits,
  onMounted,
  defineProps,
  nextTick,
  getCurrentInstance,
} from 'vue'
import {
  focusNextField as focusNextFieldHelper,
  handleKeydownField as handleKeydownFieldHelper,
} from '@/helpers'
import { helpers, required } from '@vuelidate/validators'
import { useToast } from 'primevue/usetoast'
import errorHandler from '@/helpers/error-handler'
import FxReportContainer from '@/components/FxReportContainer'
import LaporanService from '@/services/LaporanService'
import SupplierService from '@/services/SupplierService'
import KategoriService from '@/services/KategoriService'
import ItemService from '@/services/ItemService'
import useVuelidate from '@vuelidate/core'
import { saveAs } from 'file-saver'

const toast = useToast()
const laporanService = new LaporanService()
const supplierService = new SupplierService()
const kategoriService = new KategoriService()
const itemService = new ItemService()
let dialogViewer = ref(false)
let pdfSource = ref('')
let pdfTitle = ref('PDF Viewer')
let pdfFilename = ref('')

let isLoadingExport = ref(false)
let isLoading = ref(false)
// let isLoadingExportExcel = ref(false)
let form = reactive({
  supplier: null,
  kategori: 0,
  selectedItem: [],
  item: null,
})
let submitted = ref(false)
let list_supplier = ref([])
let list_kategori = ref([])
let listItems = ref([])
let selectedSupplier = ref(null)
let selectedKategori = ref(null)

const vm = getCurrentInstance().proxy

function handleKeydownField(event, nextRef, selectAll = false) {
  handleKeydownFieldHelper(vm, event, nextRef, selectAll)
}

function focusNextField(refName, selectAll = false) {
  focusNextFieldHelper(vm, refName, selectAll)
}

const rules = {
  supplier: {
    required: helpers.withMessage('Supplier harus dipilih.', required),
  },
}

const v$ = useVuelidate(rules, form)

// eslint-disable-next-line no-unused-vars
const emit = defineEmits(['toggle'])
defineProps({
  displayTitle: {
    type: Boolean,
    default: true,
  },
})

onMounted(async () => {
  const supp = await supplierService.get()
  list_supplier.value = supp.data

  const ctg = await kategoriService.getItemKategory()
  list_kategori.value = ctg.data

  // const res = await itemService.get()
  // list_item.value = res.data.data
  // set initial focus to supplier after DOM updates (use helper)
  await nextTick()
  setTimeout(() => {
    focusNextField('supplier')
  }, 100)
})

async function searchSupplier(event) {
  const query = event?.query ?? ''
  try {
    const res = await supplierService.getList(query)
    // keep only suppliers that include query in name
    list_supplier.value = res.data.filter((s) =>
      s.name.toLowerCase().includes(query.toLowerCase())
    )
  } catch (err) {
    errorHandler(err, 'Data Supplier', toast)
  }
}

async function onSelectSupplier() {
  form.supplier = selectedSupplier.value?.id || null
  list_supplier.value = []
  await nextTick()
  // focus next field
  setTimeout(() => focusNextField('kategori'), 100)
}

async function searchKategori(event) {
  const query = event?.query ?? ''
  try {
    const res = await kategoriService.getItemKategory(query)
    // filter unique and matching
    const uniqueData = res.data.filter(
      (value, index, self) =>
        index === self.findIndex((item) => item.id === value.id)
    )
    list_kategori.value = uniqueData
  } catch (err) {
    errorHandler(err, 'Data Kategori', toast)
  }
}

async function onSelectKategori() {
  form.kategori = selectedKategori.value?.id || null
  list_kategori.value = []
  await nextTick()
  setTimeout(() => focusNextField('item'), 100)
}

function searchItem(event) {
  isLoading.value = true
  const query = (event?.query || '').toString()
  itemService
    .getListCategory(query, form.kategori)
    .then((res) => {
      const q = query.toLowerCase()
      listItems.value = res.data.filter((it) => {
        const name = (it.name || '').toString().toLowerCase()
        const sku = (it.sku || '').toString().toLowerCase()
        const barcode = (it.barcode_1 || '').toString().toLowerCase()
        return name.includes(q) || sku.includes(q) || barcode.includes(q)
      })
    })
    .catch((err) => {
      errorHandler(err, 'Data Item', toast)
    })
    .finally(() => (isLoading.value = false))
}

function selectItem() {
  form.item = form.selectedItem.id
  setTimeout(() => focusNextField('btnCetak'), 50)
}

function autoCompleteKeydown(event) {
  if (event.key === 'Enter') {
    event.preventDefault()
    if (form.selectedItem && form.selectedItem.id) {
      focusNextField('btnCetak')
      return
    }

    if (listItems.value && listItems.value.length > 0) {
      form.selectedItem = listItems.value[0]
      selectItem()
      setTimeout(() => focusNextField('btnCetak'), 50)
      return
    }

    handleKeydownField(event, 'btnCetak', true)
  } else {
    handleKeydownField(event, 'btnCetak', true)
  }
}

function onExport(isFormValid) {
  isLoadingExport.value = true

  submitted.value = true
  if (!isFormValid) {
    isLoadingExport.value = false
    return
  }

  if (!form.kategori) {
    form.kategori = 0
  }

  if (!form.item) {
    form.item = 0
  }
  laporanService
    .StockSupplier(form.supplier, form.kategori, form.item)
    .then((res) => {
      const blob = new Blob([res.data], { type: 'application/pdf' })
      const pdfUrl = URL.createObjectURL(blob)
      pdfSource.value = pdfUrl + '#toolbar=0'
      pdfFilename.value = 'laporan-stock-supplier.pdf'
      pdfTitle.value = 'PDF Viewer (' + pdfFilename.value + ')'
      dialogViewer.value = true
    })

    .catch((err) => {
      errorHandler(err, 'Laporan Stok Supplier', toast)
    })
    .finally(() => (isLoadingExport.value = false))
}

function onPrintPdf() {
  const iframe = document.createElement('iframe')
  iframe.src = pdfSource.value
  iframe.style.display = 'none'
  document.body.appendChild(iframe)

  // Use onload to make pdf preview work on firefox
  iframe.onload = () => {
    iframe.contentWindow.focus()
    iframe.contentWindow.print()
  }
}

function onDownloadPdf() {
  saveAs(pdfSource.value, pdfFilename.value)
}

function onToggle() {
  emit('toggle')
}

function resetForm() {
  submitted.value = false
  form.supplier = null
  form.kategori = null
}
</script>

<template>
  <fx-report-container
    title="Laporan Inventory per Supplier"
    :loading="isLoadingExport"
    :display-title="displayTitle"
    @toggle="onToggle"
    @export="onExport"
  >
    <template #content>
      <form @submit.prevent="onExport(!v$.$invalid)">
        <div class="formgrid grid">
          <div class="field col-12">
            <div class="font-bold">Filter</div>
          </div>
          <div class="field col-12 md:col-3">
            <AutoComplete
              ref="supplier"
              v-model="selectedSupplier"
              class="w-full"
              input-class="w-full"
              placeholder="Pilih Supplier"
              :suggestions="list_supplier"
              field="name"
              :class="{ 'p-invalid': v$.supplier.$invalid && submitted }"
              @complete="searchSupplier"
              @item-select="onSelectSupplier"
              @focus="searchSupplier({ query: '' })"
              @keydown="handleKeydownField($event, 'kategori')"
            />
            <small
              v-if="
                (v$.supplier.$invalid && submitted) ||
                v$.supplier.$pending.$response
              "
              class="p-error"
              >{{ v$.supplier.required.$message }}</small
            >
          </div>
          <div class="field col-12 md:col-3">
            <AutoComplete
              ref="kategori"
              v-model="selectedKategori"
              input-class="w-full"
              placeholder="Pilih Kategori"
              :suggestions="list_kategori"
              field="name"
              class="w-full"
              @complete="searchKategori"
              @item-select="onSelectKategori"
              @focus="searchKategori({ query: '' })"
              @keydown="handleKeydownField($event, 'item')"
            />
          </div>
          <div class="field col-12 md:col-3">
            <AutoComplete
              ref="item"
              v-model="form.selectedItem"
              class="w-full"
              input-class="w-full"
              placeholder="Search Item"
              :suggestions="listItems"
              field="name"
              :minLength="0"
              :forceSelection="true"
              @focus="searchItem({ query: '' })"
              @complete="searchItem"
              @item-select="selectItem"
              @keydown="autoCompleteKeydown"
            >
              <template #item="slotProps">
                <div>
                  <b>{{ slotProps.item.sku }}</b>
                </div>
                <div>{{ slotProps.item.name }}</div>
              </template>
            </AutoComplete>
          </div>
          <div class="field col-12 md:col-3">
            <Button
              ref="btnCetak"
              :loading="isLoadingExport"
              type="submit"
              icon="pi pi-file-pdf"
              class="ml-2 mb-0 md:mb-2 xl:mb-0"
              label="Cetak"
            />
            <Button
              type="reset"
              icon="pi pi-refresh"
              label="Reset"
              class="p-button-outlined ml-2 btn-2xl-mt-2"
              @click="resetForm"
            />
          </div>
        </div>
      </form>
    </template>
  </fx-report-container>
  <Dialog
    :header="pdfTitle"
    v-model:visible="dialogViewer"
    :breakpoints="{ '960px': '75vw', '640px': '90vw' }"
    :style="{ width: '90vw' }"
    :modal="true"
  >
    <div>
      <embed
        :src="pdfSource"
        type="application/pdf"
        width="100%"
        height="600px"
      />
    </div>
    <template #footer>
      <Button
        type="button"
        label="Download"
        icon="pi pi-download"
        class="p-button-text mr-2"
        @click="onDownloadPdf"
      />
      <Button
        type="button"
        label="Print"
        icon="pi pi-print"
        @click="onPrintPdf"
      />
    </template>
  </Dialog>
</template>
